import React, { useMemo, useCallback } from "react";
import { atom, useSetRecoilState } from "recoil";
import { useNavigate, useLocation } from "react-router-dom";
import { Sidebar, SidebarMenuSection } from "@mlpa/core-ui-core-open_ocean_ui";
import { useResetGridFilters } from "components/templates/dataGridTable/serverSide/gridStatePersist";
import routes from "routes/routes";
import { getBrandingConfig, getAuth0OrgManagementUri } from "authentication/runtimeConfig";
import BackendServiceOverview from "./BackendServiceOverview";
import { Route } from "routes/utils/types";
import { APP_VERSION } from "../../../version";

interface MainLayoutSidebarProps {
  expanded: boolean;
  onToggle: () => void;
  userInitials: string;
  userName?: string;
  userEmail?: string;
  currentPath: string;
  authorizedRoutes: Route[];
}

// Intermediate shape: keeps the menuIcon function reference around so it can be
// re-invoked with the correct `selected` flag once we know which item matches
// the current path, instead of baking the icon in once with selected: false.
interface SidebarMenuItemSource {
  id: string;
  menuIconFn?: Route["menuIcon"];
  label: string;
  path?: string;
  statusLabel?: string;
  selected: boolean;
}

interface SidebarMenuSectionSource {
  title: string;
  items: SidebarMenuItemSource[];
}
export const resetAllCVEFiltersAtom = atom<boolean>({
  key: "resetAllCVEFiltersAtom",
  default: false,
});
export const MainLayoutSidebar: React.FC<MainLayoutSidebarProps> = React.memo(
  ({ expanded, onToggle, userInitials, userName, userEmail, currentPath }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const fullPath = location.pathname;
    const [modalOpen, setModalOpen] = React.useState(false);
    const setResetAllCVEFilters = useSetRecoilState(resetAllCVEFiltersAtom);
    const baseSections = useMemo<SidebarMenuSectionSource[]>(() => {
      const getStatusLabel = (path?: string) => (path === "securityCenter/vulnerabilities" ? "Beta" : undefined);

      return routes()
        .flatMap(
          (route) =>
            route.children
              ?.filter((childRoute) => childRoute.menuItem)
              .map((childRoute) => {
                // Routes with menuSection expand their menuItem children as separate sidebar items
                if (childRoute.menuSection && childRoute.children) {
                  const sectionItems = childRoute.children
                    .filter((child) => child.menuItem)
                    .map((child) => {
                      const childPath = child.path
                        ? `${childRoute.path.replace(/^\//, "")}/${child.path}`
                        : childRoute.path.replace(/^\//, "");
                      return {
                        id: childPath,
                        menuIconFn: child.menuIcon,
                        label: child.label,
                        path: childPath,
                        statusLabel: getStatusLabel(childPath),
                        selected: false,
                      };
                    });
                  return {
                    title: childRoute.label,
                    items: sectionItems,
                  };
                }
                // Default: single-item section
                return {
                  title: childRoute.label,
                  items: [
                    {
                      id: childRoute.path,
                      menuIconFn: childRoute.menuIcon,
                      label: childRoute.label,
                      path: childRoute.path,
                      statusLabel: getStatusLabel(childRoute.path.replace(/^\//, "")),
                      selected: false,
                    },
                  ],
                };
              }) ?? []
        )
        .filter((section) => !!section && typeof section.title === "string" && Array.isArray(section.items)) as SidebarMenuSectionSource[];
    }, []);

    const sections = useMemo<SidebarMenuSection[]>(() => {
      return baseSections.map((section) => {
        // Find the best matching item (longest path match) to avoid
        // index routes matching when a more specific sibling also matches
        let bestMatchIndex = -1;
        let bestMatchLength = 0;

        section.items.forEach((item, index) => {
          if (!item.path) return;
          const normalizedItemPath = item.path.replace(/^\//, "");
          const normalizedFullPath = fullPath.replace(/^\//, "");
          if (
            (normalizedFullPath === normalizedItemPath || normalizedFullPath.startsWith(normalizedItemPath + "/")) &&
            normalizedItemPath.length > bestMatchLength
          ) {
            bestMatchIndex = index;
            bestMatchLength = normalizedItemPath.length;
          }
        });

        return {
          ...section,
          items: section.items.map((item, index) => {
            const selected = index === bestMatchIndex;
            return {
              id: item.id,
              label: item.label,
              path: item.path,
              statusLabel: item.statusLabel,
              selected,
              icon: item.menuIconFn ? item.menuIconFn({ selected }) : <></>,
            };
          }),
        } as SidebarMenuSection;
      });
    }, [baseSections, fullPath]);

    const resetGridFilters = useResetGridFilters();

    const handleMenuItemClick = useCallback(
      (id: string, path?: string) => {
        if (path) {
          const normalizedPath = path.replace(/^\//, "");
          if (normalizedPath === "userManagement" || normalizedPath.startsWith("userManagement/")) {
            const externalUri = getAuth0OrgManagementUri();
            if (externalUri) {
              window.open(externalUri, "_blank", "noopener,noreferrer");
              return;
            }
          }
          resetGridFilters();
          // Only reset CVE filters when navigating to Security Center via the sidebar
          // itself — not on every sidebar click. Otherwise this stale flag survives
          // until Security Center is next mounted and clobbers filters set by other
          // means (e.g. deep-linking from a device's vulnerability count).
          if (normalizedPath === "securityCenter/vulnerabilities" || normalizedPath.startsWith("securityCenter/vulnerabilities/")) {
            setResetAllCVEFilters(true);
          }
          navigate(path);
        }
      },
      [navigate, resetGridFilters]
    );

    const handleUserClick = useCallback(() => {
      navigate("/myProfile");
    }, [navigate]);

    const branding = getBrandingConfig();

    const logoSrc = useMemo(() => {
      if (branding.brandId === "phyhub" && !expanded) {
        return "/assets/branding/phyhub/Phyhub_S.svg";
      }
      return branding.logoUrl;
    }, [branding.brandId, branding.logoUrl, expanded]);
    const noWidth = branding.brandId === "phyhub" ? true : false;
    return (
      <div>
        <Sidebar
          expanded={expanded}
          onToggle={onToggle}
          sections={sections}
          onMenuItemClick={handleMenuItemClick}
          userInitials={userInitials}
          userName={userName}
          userEmail={userEmail}
          versionNumber={APP_VERSION}
          onVersionClick={() => setModalOpen(true)}
          onUserClick={handleUserClick}
          logoSrc={logoSrc}
          noWidth={noWidth}
          logoLabel={branding.sidebarLabel}
          className={`brand-${branding.brandId}`}
        />
        {modalOpen && <BackendServiceOverview open={modalOpen} handleClose={() => setModalOpen(false)} />}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.expanded === nextProps.expanded &&
      prevProps.userInitials === nextProps.userInitials &&
      prevProps.userName === nextProps.userName &&
      prevProps.userEmail === nextProps.userEmail &&
      prevProps.currentPath === nextProps.currentPath
    );
  }
);

MainLayoutSidebar.displayName = "MainLayoutSidebar";
